// Pure detail → thread-item derivation (docs/06-ux-rules.md "Thread";
// veda:projects/unified-feedback-inbox/parts/p05-thread-pane.md D1-D10, P4).
// `ThreadPane/index.tsx` renders one component per item type; this module
// owns none of that — it only decides WHICH items exist and in WHAT order,
// mirroring `inboxRowStatus.ts`'s "pure derivation, tested directly"
// pattern elsewhere in this feature.
//
// Ordering: every item carries an `at` ISO timestamp; the final list is a
// stable sort on that timestamp (ties broken by insertion order via an
// explicit index, not relied on JS engine sort stability alone).
// `resolutionHistory` yields one resolved_marker / reopened per entry, except
// the resolve the restrict produced — the `restricted` note stands for it.
//
// Decisions made here that the spec left implicit (see execution report
// "Decisions I made"):
//   - `automated_ack` is synthesized only for plain-feedback (`kind ===
//     'feedback'`) thumbs-down items with a guest opener, immediately after
//     the guest item, at the item's own `date` (responded_at) — it's the
//     immediate client-side auto-response fired at submission before any
//     human involvement. RecoveryAI threads (`kind === 'recovery_ai'`) get
//     no ack note: the guest opener goes straight into the RAI
//     chat/email/escalation flow (docs/08-decision-log 2026-09-18: ack
//     scoped to feedback).
//   - `resolved_marker` / `reopened` / `escalation` / `merchant_email`
//     items only apply to thumbs-down items — a thumbs-up row renders the
//     single `positive` note and nothing else (D7's "one note only" reading
//     wins over the older "guest item + ack" phrasing it superseded).
//   - `messages` role `merchant` turns are NOT modeled as thread items —
//     `operatorEmails` is the exclusive source for `merchant_email` per
//     D1/D9; a stray `merchant`-role turn (none exist in today's fixtures)
//     is silently hidden alongside `system`/`tool_call`.
import type {
  FeedbackInboxActor,
  FeedbackInboxDetail,
  FeedbackInboxResolutionEvent,
  ThreadTurn,
} from 'api/feedbackInbox.types'
import { buildTranslate } from 'locales'

const t = buildTranslate('feedback_inbox.thread')
const tComposer = buildTranslate('feedback_inbox.composer')

export type ThreadItem =
  | {
      id: string
      type: 'guest'
      at: string
      name: string
      initials: string
      text: string
      // True only for the guest's opening rating comment — the prototype
      // wraps that one in curly quotes, but not later chat turns
      // (VISUAL-SPEC.md §1 "Quote marks"; `text` itself stays unquoted here,
      // GuestMessage applies the locale-owned quote marks at render time).
      quoted?: boolean
    }
  | { id: string; type: 'nocomment'; at: string; text: string }
  | { id: string; type: 'positive'; at: string; text: string }
  | { id: string; type: 'automated_ack'; at: string; text: string }
  | { id: string; type: 'ai_chat'; at: string; text: string }
  | {
      id: string
      type: 'ai_email'
      at: string
      text: string
      to: string
      subject: string
    }
  | { id: string; type: 'escalation'; at: string; text: string }
  | {
      id: string
      type: 'merchant_email'
      at: string
      authorName: string
      to: string
      subject: string
      body: string
    }
  | { id: string; type: 'resolved_marker'; at: string; name: string }
  | { id: string; type: 'reopened'; at: string; name: string }
  | { id: string; type: 'restricted'; at: string; text: string }

// A plain `Omit<ThreadItem, 'id'>` collapses the union down to only its
// SHARED keys (`keyof` of a union is an intersection) — this distributes
// the `Omit` over each member instead, so every type-specific field
// (`text`, `name`, `authorName`, ...) survives.
type ThreadItemWithoutId = ThreadItem extends infer T
  ? T extends ThreadItem
    ? Omit<T, 'id'>
    : never
  : never

// First-letter-of-first-word + first-letter-of-last-word, uppercased —
// matches the guest bubble avatar rule (docs/06-ux-rules.md "Avatar rule").
export function initialsFor(name: string | null): string {
  if (!name) return '?'
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  const first = parts[0][0] ?? ''
  const last = parts.length > 1 ? parts[parts.length - 1][0] ?? '' : ''
  return (first + last).toUpperCase() || '?'
}

function defaultEmailSubject(orderNumber: string | null): string {
  return orderNumber
    ? tComposer('subject_default', { orderNumber })
    : tComposer('subject_default_fallback')
}

interface Timestamped {
  item: ThreadItem
  at: string
}

function isVisibleTurn(turn: ThreadTurn): boolean {
  return turn.role !== 'system' && turn.role !== 'tool_call'
}

// The guest's name only with PII consent — without it nexus still sends a
// degraded "Jane D." name, which this sentence deliberately doesn't use.
function restrictedGuestName(detail: FeedbackInboxDetail): string {
  const { firstName, name } = detail.customer
  return firstName !== null && name !== null
    ? name
    : t('restricted_note.guest_fallback')
}

// `fallbackKey` differs per call site: the restricted note starts a sentence
// (capitalised), the markers don't.
function actorName(
  by: FeedbackInboxActor,
  fallbackKey: 'resolved_by_fallback' | 'restricted_note.operator_fallback'
): string {
  if (by.kind === 'ai') return t('recovery_ai_name')
  return by.name || t(fallbackKey)
}

// No FK ties a restrict to its resolve, and both stamp at second precision
// (so they tie or invert) — pair by the nearest `customer_restricted` resolve.
// Close-others cases share the type but have no note, so keep their markers.
function restrictPairedResolve(
  detail: FeedbackInboxDetail
): FeedbackInboxResolutionEvent | null {
  if (!detail.restrictedEvent) return null
  const restrictedAt = Date.parse(detail.restrictedEvent.at)
  let nearest: FeedbackInboxResolutionEvent | null = null
  let nearestGap = Infinity
  for (const entry of detail.resolutionHistory) {
    if (entry.action !== 'resolved') continue
    if (entry.resolutionType !== 'customer_restricted') continue
    const gap = Math.abs(Date.parse(entry.at) - restrictedAt)
    if (gap < nearestGap) {
      nearest = entry
      nearestGap = gap
    }
  }
  return nearest
}

export function buildThreadItems(detail: FeedbackInboxDetail): ThreadItem[] {
  const entries: Timestamped[] = []
  const name = detail.customer.name ?? t('unknown_guest_fallback')
  const firstName =
    detail.customer.firstName ?? detail.customer.name?.split(' ')[0] ?? name
  const initials = initialsFor(detail.customer.name)

  const push = (item: ThreadItemWithoutId) => {
    entries.push({ item: { ...item, id: '' } as ThreadItem, at: item.at })
  }

  if (detail.rating === 'up') {
    push({
      type: 'positive',
      at: detail.date,
      text: t('thumbs_up_note', { firstName }),
    })
  } else {
    const visibleMessages = detail.messages.filter(isVisibleTurn)
    const firstUserTurn = visibleMessages.find(turn => turn.role === 'user')
    const openerText = detail.preview ?? firstUserTurn?.content ?? null

    if (openerText === null) {
      push({
        type: 'nocomment',
        at: detail.date,
        text: t('no_comment_note', { firstName }),
      })
    } else {
      push({
        type: 'guest',
        at: detail.date,
        name,
        initials,
        text: openerText,
        quoted: true,
      })
      // Ack is a plain-feedback-only concept (see file header) — RecoveryAI
      // threads go straight from the guest opener into the RAI
      // chat/email/escalation flow.
      if (detail.kind === 'feedback') {
        push({
          type: 'automated_ack',
          at: detail.date,
          text: t('automated_ack'),
        })
      }

      // RAI conversations derive the opener from the first user turn — skip
      // that exact turn below so it isn't rendered a second time. Plain
      // feedback's opener is the rating comment (a separate field, not tied
      // to any turn), so every user turn there is additional content.
      const remaining =
        detail.kind === 'recovery_ai' && firstUserTurn
          ? visibleMessages.filter(turn => turn !== firstUserTurn)
          : visibleMessages

      remaining.forEach(turn => {
        if (turn.role === 'user') {
          push({
            type: 'guest',
            at: turn.timestamp,
            name,
            initials,
            text: turn.content,
          })
        } else if (turn.role === 'ai') {
          if (detail.channel === 'email') {
            push({
              type: 'ai_email',
              at: turn.timestamp,
              text: turn.content,
              to: name,
              subject: defaultEmailSubject(detail.orderNumber),
            })
          } else {
            push({ type: 'ai_chat', at: turn.timestamp, text: turn.content })
          }
        }
        // 'merchant' turns: not modeled — see file header.
      })
    }

    if (detail.escalation) {
      const { reason } = detail.escalation
      push({
        type: 'escalation',
        at: detail.escalation.at,
        // `reason` is already a complete sentence (nexus
        // EscalationTriggerCopy, shared with the escalation email), so it
        // renders verbatim — wrapping it in our own clause broke the grammar.
        text: reason || t('escalation_body_unknown'),
      })
    }

    detail.operatorEmails.forEach(email => {
      push({
        type: 'merchant_email',
        at: email.sentAt,
        // `authorName` is a non-nullable wire string, but the backend spells
        // "the sending operator has no resolvable identity" as `''` (Email
        // carries no merchant_user FK) rather than a null the type could
        // catch — same generic-operator fallback the resolved/reopened
        // markers below already use for that situation.
        authorName: email.authorName || t('resolved_by_fallback'),
        to: name,
        subject: email.subject,
        body: email.body,
      })
    })

    const restrictPaired = restrictPairedResolve(detail)
    detail.resolutionHistory.forEach(entry => {
      if (entry === restrictPaired) return
      push({
        type: entry.action === 'resolved' ? 'resolved_marker' : 'reopened',
        at: entry.at,
        name: actorName(entry.by, 'resolved_by_fallback'),
      })
    })

    if (detail.restrictedEvent) {
      push({
        type: 'restricted',
        at: detail.restrictedEvent.at,
        text: t('restricted_note.body', {
          operator: actorName(
            detail.restrictedEvent.by,
            'restricted_note.operator_fallback'
          ),
          guest: restrictedGuestName(detail),
        }),
      })
    }
  }

  const sorted = entries
    .map((entry, index) => ({ entry, index }))
    .sort((a, b) => {
      const diff = Date.parse(a.entry.at) - Date.parse(b.entry.at)
      return diff !== 0 ? diff : a.index - b.index
    })
    .map(({ entry }) => entry.item)

  return sorted.map((item, index) => ({
    ...item,
    id: `${detail.id}-${item.type}-${index}`,
  }))
}
